import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import { applicationFormProblem, applicationInput, emptyApplicationForm, formFromApplication, type ApplicationForm } from '../lib/applications'
import type { CostApplication } from '../types'

interface Props {
  open: boolean
  mode: 'create' | 'edit'
  application?: CostApplication | null
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

/** The period and a note of a payment application (1b-A spec §2); the period defaults to the previous month. */
const ApplicationDialog: React.FC<Props> = ({ open, mode, application, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<ApplicationForm>(() => emptyApplicationForm())
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    if (!open) return
    setForm(application ? formFromApplication(application) : emptyApplicationForm())
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const problem = applicationFormProblem(form)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(applicationInput(form))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'CostControl_application_new' : 'CostControl_application_edit')}</DialogTitle>
          {mode === 'create' && <DialogDescription>{t('CostControl_application_dialog_hint')}</DialogDescription>}
        </DialogHeader>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5">
            <Label htmlFor="cc-app-from">{t('CostControl_application_field_periodFrom')} *</Label>
            <IsoDatePicker id="cc-app-from" value={form.periodFrom} onChange={(v) => setForm((f) => ({ ...f, periodFrom: v }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-app-to">{t('CostControl_application_field_periodTo')} *</Label>
            <IsoDatePicker id="cc-app-to" value={form.periodTo} onChange={(v) => setForm((f) => ({ ...f, periodTo: v }))} invalid={touched && problem === 'CostControl_error_application_period_order'} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cc-app-note">{t('CostControl_application_field_note')}</Label>
            <Textarea id="cc-app-note" rows={3} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          </div>
          {touched && problem && <p className="text-sm text-destructive sm:col-span-2">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'Common_create' : 'Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default ApplicationDialog
